import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Form, Spinner, Alert } from 'react-bootstrap';
import { FaTasks, FaSearch } from 'react-icons/fa';
import MaintenanceTaskCard from '../components/MaintenanceTaskCard';
import taskApi from '../services/taskApi';
import DesktopLayout from '../layouts/DesktopLayout';
import useLanguage from '../hooks/useLanguage';
import { TASK_TYPE_FILTERS } from '../config/taskTypes';
import { AuthContext } from '../context/AuthContext';
import { TASK_MANAGER_ROLES } from '../config/roleConfig';

const PRIORITY_ORDER = ['critical', 'urgent', 'high', 'medium', 'low'];

const PRIORITY_EMOJIS = {
  critical: '🔴',
  urgent:   '🟠',
  high:     '🟡',
  medium:   '🟢',
  low:      '🔵',
};

const MaintenanceTasks = () => {
  const { t, isArabic } = useLanguage();
  const { user } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isSupervisor = user?.role === 'maintenance-supervisor';
  // Undoing a start is a supervisor's correction (tasks/cleaning-performance-report.md D6) — the
  // crew no longer see «تراجع»; the server refuses it for them with TASK_010 anyway.
  const canUndoStart = TASK_MANAGER_ROLES.includes(user?.role);
  const [dateFilter, setDateFilter] = useState('all');
  // The crew open on «كل المهام» (founder, 23 Sep — tasks/cleaning-performance-report.md): a partner
  // arriving at a clean the lead already started must see it to tap «انضممت», and the lead is usually
  // its assignee. The supervisor keeps «المكلفة لي» — the selector is hidden for them and /tasks is
  // where they see everyone's work.
  const [assignmentFilter, setAssignmentFilter] = useState(isSupervisor ? 'mine' : 'all');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'cleaning' | 'maintenance' | 'restock'
  const [searchQuery, setSearchQuery] = useState('');
  const [clockOffsetMs, setClockOffsetMs] = useState(0);
  const [searchParams] = useSearchParams();
  const [activeNavItem, setActiveNavItem] = useState('my-tasks');

  const loadTasks = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    setError(null);

    try {
      const apiFilters = {};
      // The SERVER resolves the window in the business timezone — see Tasks.jsx.
      if (['today', 'week'].includes(dateFilter)) {
        apiFilters.businessRange = dateFilter;
      }
      // 'all' — no date filters, API returns everything

      // Server-side assignment filter
      if (assignmentFilter === 'mine' && user?.userId) {
        apiFilters.assignedTo = user.userId;
      }

      const response = await taskApi.getTasks(apiFilters);
      // Server clock minus the phone's, for the cleaning timer (tasks/cleaning-timer.md D6).
      const serverNow = Date.parse(response.data.serverTime);
      setClockOffsetMs(Number.isNaN(serverNow) ? 0 : serverNow - Date.now());
      const fetched = (response.data.tasks || []).filter(
        task => task.status !== 'cancelled'
      );

      // Sort by scheduled date ascending within each priority group
      fetched.sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
      setTasks(fetched);
    } catch (err) {
      console.error('MaintenanceTasks load error:', err);
      setError(t('tasks.listView.errorLoading'));
    }

    if (showSpinner) setLoading(false);
  };

  /**
   * Arriving from a guest-checkout notification: `/#/my-tasks?ticket=HM-0219`.
   * tasks/manual-guest-checkout-event.md D36.
   *
   * ⚠️ THE FILTERS MUST BE WIDENED, not just the search box filled. The default view is "my tasks"
   * within today, but the notification is sent to the whole crew whenever the cleaning task is
   * UNASSIGNED (D35) — which is the normal case, because nothing auto-assigns them. Setting only
   * the search term would land the tap on an empty list for exactly the people the message was
   * written for, and an empty screen reads as "the notification was wrong".
   *
   * Runs once per ticket value: the person is free to change any filter afterwards, and re-applying
   * on every render would fight them.
   */
  // `ticket` is the normal case; `task` is the fallback for legacy tasks that predate ticket ids —
  // an empty `?ticket=` would skip this effect entirely and strand the tap on the default view.
  const deepLinkRef = searchParams.get('ticket') || searchParams.get('task');
  useEffect(() => {
    if (!deepLinkRef) return;
    setSearchQuery(deepLinkRef);
    setAssignmentFilter('all');
    setDateFilter('all');
    // ⚠️ The TYPE filter has to be cleared too, and it is the one most likely to be set. The service
    // worker navigates an already-open tab by changing the hash, which does NOT remount this page —
    // so a crew member who left the chip on «صيانة» yesterday taps a cleaning notification and lands
    // on an empty list. Every filter this effect does not reset is one that can hide the task.
    setTypeFilter('all');
  }, [deepLinkRef]);

  useEffect(() => {
    loadTasks();
  }, [dateFilter, assignmentFilter]); // eslint-disable-line

  // One tap when the crew picks the job up. Ops reads it live: the task table shows
  // «قيد التنفيذ», arrival readiness flips the cleaning column, and the checkout push
  // for that unit goes quiet because someone has clearly taken it.
  const handleStart = async (taskId) => {
    await taskApi.updateTaskStatus(taskId, 'in-progress');
    await loadTasks(false);
  };

  // Undo a start. Back to 'assigned' when the task has an owner, 'pending' when it does
  // not — restoring the state the card was in, not a generic reset.
  const handleRevert = async (taskId, targetStatus) => {
    await taskApi.updateTaskStatus(taskId, targetStatus);
    await loadTasks(false);
  };

  // «انضممت» — the partner adds themselves to a clean already under way; the clock is untouched.
  const handleJoin = async (taskId) => {
    await taskApi.joinClean(taskId);
    await loadTasks(false);
  };

  const handleComplete = async (taskId, completionData = {}) => {
    await taskApi.updateTaskStatus(taskId, 'completed', completionData);
    await loadTasks(false);
  };

  let filteredTasks = tasks;

  // Apply type filter (client-side — the crew list is small, no reload needed)
  if (typeFilter !== 'all') {
    filteredTasks = filteredTasks.filter(task => task.type === typeFilter);
  }

  // Apply search filter (client-side)
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filteredTasks = filteredTasks.filter(task =>
      task.ticketId?.toLowerCase().includes(query) ||
      task.title?.toLowerCase().includes(query) ||
      task.displayLocationName?.toLowerCase().includes(query) ||
      task.assignedTo?.name?.toLowerCase().includes(query)
    );
  }

  // Group active tasks by priority, completed at the bottom
  const activeTasks = filteredTasks.filter(task => task.status !== 'completed');
  const completedTasks = filteredTasks.filter(task => task.status === 'completed');

  const groupedByPriority = PRIORITY_ORDER.reduce((acc, priority) => {
    const group = activeTasks.filter(task => task.priority === priority);
    if (group.length > 0) acc[priority] = group;
    return acc;
  }, {});

  const totalActive = activeTasks.length;
  const totalCompleted = completedTasks.length;

  const dir = isArabic ? 'rtl' : 'ltr';

  if (loading) {
    return (
      <DesktopLayout
        activeNavItem={activeNavItem}
        setActiveNavItem={setActiveNavItem}
        headerTitle={t('tasks.maintenance.pageTitle')}
      >
        <Container className="text-center py-5" dir={dir}>
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">{t('tasks.listView.loading')}</p>
        </Container>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle={t('tasks.maintenance.pageTitle')}
    >
      <Container fluid className="py-3 px-3" dir={dir}>

        {/* Search */}
        <div className="position-relative mb-3">
          <Form.Control
            type="text"
            size="sm"
            placeholder={t('tasks.maintenance.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            dir={dir}
            style={{ paddingRight: isArabic ? '30px' : undefined, paddingLeft: isArabic ? undefined : '30px' }}
          />
          <FaSearch
            className="position-absolute text-muted"
            style={{ top: '50%', transform: 'translateY(-50%)', left: isArabic ? 'auto' : '10px', right: isArabic ? '10px' : 'auto', pointerEvents: 'none' }}
            size={12}
          />
        </div>

        {/* Filters + summary */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="text-muted small">
            {totalActive > 0
              ? <span>{totalActive} {t('tasks.maintenance.tasksRemaining')} • <span className="text-success">{totalCompleted} {t('tasks.status.completed')}</span></span>
              : <span className="text-success fw-bold">✓ {t('tasks.maintenance.allDone')}</span>
            }
          </div>
          <div className="d-flex gap-2">
            {!isSupervisor && (
              <Form.Select
                size="sm"
                className="touch-select"
                value={assignmentFilter}
                onChange={(e) => setAssignmentFilter(e.target.value)}
                style={{ width: 'auto' }}
                dir={dir}
              >
                <option value="all">{t('tasks.maintenance.filterAll')}</option>
                <option value="mine">{t('tasks.maintenance.filterMyTasks')}</option>
              </Form.Select>
            )}
            <Form.Select
              size="sm"
              className="touch-select"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{ width: 'auto' }}
              dir={dir}
            >
              <option value="all">{t('tasks.maintenance.filterAll')}</option>
              <option value="today">{t('tasks.listView.filters.today')}</option>
              <option value="week">{t('tasks.listView.filters.week')}</option>
            </Form.Select>
            <Form.Select
              size="sm"
              className="touch-select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{ width: 'auto' }}
              dir={dir}
            >
              <option value="all">{t('tasks.page.typeAll')}</option>
              {TASK_TYPE_FILTERS.map(type => (
                <option key={type} value={type}>{t(`tasks.type.${type}`)}</option>
              ))}
            </Form.Select>
          </div>
        </div>

        {/* Error */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Empty state */}
        {filteredTasks.length === 0 && !error && (
          <div className="text-center py-5 text-muted">
            <FaTasks size={48} className="mb-3" />
            <h5>{t('tasks.listView.emptyTitle')}</h5>
            <p>{t('tasks.maintenance.emptyText')}</p>
          </div>
        )}

        {/* Active tasks grouped by priority */}
        {PRIORITY_ORDER.map(priority => {
          const group = groupedByPriority[priority];
          if (!group) return null;
          return (
            <section key={priority} className="mb-4">
              <h6 className="mb-2 fw-bold" style={{ color: 'var(--bs-secondary)' }}>
                {PRIORITY_EMOJIS[priority]} {t(`tasks.listView.sections.${priority}`)} ({group.length})
              </h6>
              {group.map(task => (
                <MaintenanceTaskCard
                  key={task._id}
                  task={task}
                  onStart={handleStart}
                  onRevert={canUndoStart ? handleRevert : undefined}
                  onJoin={handleJoin}
                  onComplete={handleComplete}
                  onTaskChanged={() => loadTasks(false)}
                  clockOffsetMs={clockOffsetMs}
                />
              ))}
            </section>
          );
        })}

        {/* Completed tasks — collapsed at bottom */}
        {completedTasks.length > 0 && (
          <section className="mb-4">
            <h6 className="mb-2 text-muted">
              ✅ {t('tasks.listView.sections.completed')} ({completedTasks.length})
            </h6>
            {completedTasks.map(task => (
              <MaintenanceTaskCard
                key={task._id}
                task={task}
                onComplete={handleComplete}
                onTaskChanged={() => loadTasks(false)}
              />
            ))}
          </section>
        )}

      </Container>
    </DesktopLayout>
  );
};

export default MaintenanceTasks;
